import { Composition } from "../../editorCore/composition/composition";
import { CompositionStore } from "../store/CompositionStore";
import { CANVAS_ID, COMPOSITION_CONTAINER_ID } from "../config/config";
import {hex} from "../../editorCore/types";

export class CompositionService {
    private static _composition: Composition | null = null;

    public static get composition(): Composition {
        if (!this._composition) {
            throw new Error("Composition not initialized. Call CompositionService.initialize() first.");
        }
        return this._composition;
    }

    /**
     * Check if composition is initialized
     */
    static isInitialized(): boolean {
        return this._composition !== null;
    }

    /**
     * Initialize the composition singleton and mount it to the canvas
     */
    static async initialize(): Promise<void> {
        if (this._composition) {
            console.warn("Composition already initialized");
            return;
        }

        // Create new composition instance
        this._composition = new Composition({
            width: 1920,
            height: 1080,
            background: '#000000',
            backend: 'webgl',
        });

        // Set up event listeners to sync with CompositionStore
        this._composition.on('play', (frame) => {
            CompositionStore.setIsPlaying(true);
        });

        this._composition.on('pause', (frame) => {
            CompositionStore.setIsPlaying(false);
        });

        this._composition.on('currentframe', (frame) => {

            const frameNumber = frame.detail as number;

            const seconds = frameNumber / this._composition!.fps;

            CompositionStore.setCurrentTime(seconds);
        });

        // Mount the composition to the canvas
        this.mountComposition();

        console.log("Composition service initialized and mounted");
    }

    /**
     * Mount the composition to the canvas element
     */
    private static mountComposition(): void {
        const container = document.getElementById(COMPOSITION_CONTAINER_ID) as HTMLDivElement;
        const player = document.getElementById(CANVAS_ID) as HTMLDivElement;

        if (!container || !player) {
            console.error("Could not find composition container or canvas element");
            return;
        }

        // Mount the composition to the player element
        this.composition.attachPlayer(player);

        // Calculate and apply scaling
        const scale = Math.min(
            container.clientWidth / this.composition.settings.width,
            container.clientHeight / this.composition.settings.height,
        );

        // Set the player dimensions and scaling
        player.style.width = `${this.composition.settings.width}px`;
        player.style.height = `${this.composition.settings.height}px`;
        player.style.transform = `scale(${scale})`;
        player.style.transformOrigin = 'center';

        console.log(`Composition mounted with scale: ${scale}`);
    }

    /**
     * Update the composition scaling when container size changes
     */
    static updateScaling(): void {
        if (!this._composition) return;

        const container = document.getElementById(COMPOSITION_CONTAINER_ID) as HTMLDivElement;
        const player = document.getElementById(CANVAS_ID) as HTMLDivElement;

        if (!container || !player) return;

        const scale = Math.min(
            container.clientWidth / this.composition.settings.width,
            container.clientHeight / this.composition.settings.height,
        );

        player.style.transform = `scale(${scale})`;
    }

    /**
     * Cleanup the composition instance
     */
    static cleanup(): void {
        if (this._composition) {
            // Detach from DOM if mounted
            const player = document.getElementById(CANVAS_ID) as HTMLDivElement;
            if (player) {
                this._composition.detachPlayer(player);
            }
            this._composition = null;
        }
    }

    static getCurrentTime(): number {
        if (!this._composition) return 0;
        return this.composition.frame / this.composition.fps;
    }

    static getFitDuration(): number {
        if (!this._composition) return 0;
        return this.composition.duration.seconds;
    }

    static getFps(): number {
        if (!this._composition) return 30;
        return this.composition.fps;
    }

    static async play(): Promise<void> {
        if (!this._composition) return;
        await this.composition.play();
    }

    static async pause(): Promise<void> {
        if (!this._composition) return;
        await this.composition.pause();
    }

    static async seek(time: number): Promise<void> {
        if (!this._composition) return;
        // Convert seconds to frames
        const frame = Math.round(time * this.composition.fps);
        await this.composition.seek(frame);

        console.log("Seek to time:", time, "seconds (frame:", frame, ")");
    }

    static setBackgroundColor(color: hex): void {
        if (!this._composition) return;
        this.composition.settings.background = color;
        CompositionStore.setDisplayBackgroundColor(color);
    }

    static setResolution(width: number, height: number): void {
        if (!this._composition) return;
        this.composition.settings.width = width;
        this.composition.settings.height = height;
        // Update scaling after resolution change
        this.updateScaling();
    }

    static getFonts() {
        // TODO: Implement when needed
    }

    static getMediaById(mediaId: string) {
        // TODO: Implement when needed
    }

    static addMediaToGallery(file: File | string, mimeType?: string) {
        // TODO: Implement when needed
    }

    static getClipById(clipId: string) {
        // TODO: Implement when needed
    }

    static getLayerById(layerId: string) {
        // TODO: Implement when needed
    }

    static createLayer(index?: number) {
        // TODO: Implement when needed
    }

    static deleteLayer(layerId: string) {
        // TODO: Implement when needed
    }

    static async moveClipToLayer(clipId: string, layerId: string) {
        // TODO: Implement when needed
    }

    static async addMediaToLayer(layerId: string, mediaId: string, dropTime = CompositionService.getCurrentTime()) {
        // TODO: Implement when needed
    }

    static addTextClipToLayer(layerId: string, text: string = "Hello World") {
        // TODO: Implement when needed
    }

    static deleteMediaById(mediaId: string) {
        // TODO: Implement when needed
    }

    static deleteClipById(clipId: string) {
        // TODO: Implement when needed
    }

    static deleteTransitionById(transitionId: string, layerId: string) {
        // TODO: Implement when needed
    }

    static deleteLibrettoClipAtCurrentTime() {
        // TODO: Implement when needed
    }

    static splitLibrettoClipAtCurrentTime = async () => {
        // TODO: Implement when needed
    }

    static async duplicateLibrettoClipById(librettoClipId: string) {
        // TODO: Implement when needed
    }

    static async duplicateClipById(clipId: string) {
        // TODO: Implement when needed
    }

    static moveClipLeft = () => {
        // TODO: Implement when needed
    };

    static export() {
        // TODO: Implement when needed
    }

    static async importSubtitlesSrt(srtContent: string) {
        // TODO: Implement when needed
    }

    static async addManualSubtitles(textBlocks) {
        // TODO: Implement when needed
    }

    static async addSubtitles(subtitles) {
        // TODO: Implement when needed
    }

    static async createLottieClip(options: {
        dataUrl: string;
        propertiesUrl: string;
        startTime: number;
        layerId?: string;
    }) {
        // TODO: Implement when needed
    }

    static async createTextClip(options: { text: string; startTime: number; layerId?: string }) {
        // TODO: Implement when needed
    }

    static loadFont(fontName: string, url: string) {
        // TODO: Implement when needed
    }

    static pauseUndo() {
        // TODO: Implement when needed
    }

    static resumeUndo() {
        // TODO: Implement when needed
    }

    static getTransitionById(transitionId: string) {
        // TODO: Implement when needed
    }
}
